import { useState } from 'react';

import { RAYCHAT_UPLOAD_FILE_URL } from 'utils/constants';
import { ChevronDownIcon, MessageEmojiComIcon } from 'assets/icons';
import styles from './styles.module.scss';
import AvatarFallBack from 'assets/icons/user.svg';
import useTranslation from 'hooks/useTranslation';
import { toEnDigit, convertDates, getBeutifiedText } from 'utils';
import FileMessage from '../FileMessage';
import TextMessage from '../TextMessage';
import AudioMessage from '../AudioMessage';
import VideoMessage from '../VideoMessage';

function ConversationCard({ data }) {
  const [isOpen, setIsOpen] = useState(false);
  const { page, name, convDate, chatsThread } = data;
  const chatsCount = data.chatsThread.length;

  const { getTranslation, getAppLocale } = useTranslation('ARCHIVE');
  const { t } = getTranslation();
  const locale = getAppLocale();

  const handleClickCardHeader = () => {
    setIsOpen(!isOpen);
  };

  const rederConvDate =
    locale === 'fa' ? convDate : convertDates(toEnDigit(convDate));

  const renderHeaderTitle = (
    <div className={styles.title__right}>
      <span>{name}</span>{' '}
      <span className={styles.website_text}>
        {t('CONV_CARD_IN_WEBSITE_TEXT')}
      </span>
      <a className={styles.link} target='_blank' rel='noreferrer' href={page}>
        {page}
      </a>
    </div>
  );

  const getReplyPreviewContent = (repliedChat) => {
    switch (repliedChat.type) {
      case 'image':
        return t('IMAGE_PREVIEW_LABEL');
      case 'file':
        return t('FILE_MESSAGE');
      case 'video':
        return t('VIDEO_MESSAGE');
      case 'compress':
        return t('FILE_MESSAGE');
      case 'audio':
        return t('VOICE_MESSAGE');
      default:
        return repliedChat.content;
    }
  };

  const renderReplyPreview = (id) => {
    if (!id) return null;

    const repliedChat = chatsThread.find((item) => item.id === id);
    if (!repliedChat) return null;

    // get suitable preview for each message type
    const contentToDisplay = getReplyPreviewContent(repliedChat);

    const isRTL = locale === 'fa';
    return (
      <div className={styles.reply_preview}>
        <span className={styles.inner}>
          {getBeutifiedText(contentToDisplay, 150, isRTL)}
        </span>
      </div>
    );
  };

  const renderMessageContent = (message) => {
    if (message.type === 'image') {
      return (
        <img src={`${RAYCHAT_UPLOAD_FILE_URL}/${message.content}`} alt='' />
      );
    }

    if (['file', 'compress'].includes(message.type)) {
      return (
        <FileMessage
          time={message.time}
          url={message.url}
          delivered={message.delivered}
          name={message.name}
          size={message.size}
          type={message.type}
          replyId={message.replyId}
          id={message.id}
          status={message.status}
          locale={locale}
          getMessagePreviewById={renderReplyPreview}
        />
      );
    }

    if (message.type === 'audio') {
      return (
        <AudioMessage
          id={message.id}
          url={message.url}
          time={message.time?.[locale]}
          name={message.name}
          getMessagePreviewById={renderReplyPreview}
          replyId={message.replyId}
        />
      );
    }

    if (message.type === 'video') {
      return (
        <VideoMessage
          id={message.id}
          url={message.url}
          name={message.name}
          time={message.time?.[locale]}
          getMessagePreviewById={renderReplyPreview}
          replyId={message.replyId}
        />
      );
    }

    return (
      <TextMessage
        id={message.id}
        content={message.content}
        time={message.time}
        locale={locale}
        replyId={message.replyId}
        getMessagePreviewById={renderReplyPreview}
        edited={message.edited}
        name={message.name}
      />
    );
  };

  const renderMessageCollection = (chatData) => {
    return (
      <div
        data-isAgent={chatData.isAgent}
        className={styles.message_collection}
      >
        <div className={styles.avatar_wrapper}>
          <img
            className={styles.avatar}
            src={chatData.avatar}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null; // prevents looping
              currentTarget.src = AvatarFallBack;
            }}
            alt=''
          />
        </div>
        <div className={styles.message}>{renderMessageContent(chatData)}</div>
      </div>
    );
  };

  const getLastChatDataContent = (chatData) => {
    switch (chatData.type) {
      case 'image':
        return t('IMAGE_MESSAGE');
      case 'file':
        return t('FILE_MESSAGE');
      case 'video':
        return t('VIDEO_MESSAGE');
      case 'audio':
        return t('VOICE_MESSAGE');
      default:
        return chatData.content;
    }
  };

  // renders last chat preview when conversation card is closed
  const renderLastChat = (chatData) => {
    return (
      <div data-isAgent={chatData.isAgent} className={styles.last_message}>
        <div className={styles.avatar_wrapper}>
          <img
            className={styles.avatar}
            src={chatData.avatar}
            onError={({ currentTarget }) => {
              currentTarget.onerror = null; // prevents looping
              currentTarget.src = AvatarFallBack;
            }}
            alt=''
          />
        </div>
        <div className={styles.content}>
          <p className={styles.content__inner}>
            {getLastChatDataContent(chatData)}
          </p>
        </div>
      </div>
    );
  };

  const renderChats = () => {
    if (!isOpen) {
      return renderLastChat(chatsThread[chatsThread.length - 1]);
    }

    return chatsThread.map((item) => (
      <div key={item.id}>{renderMessageCollection(item)}</div>
    ));
  };

  return (
    <div data-open={isOpen} className={styles.card}>
      {/* card header */}
      <div onClick={handleClickCardHeader} className={styles.card__header}>
        {/* right */}
        {renderHeaderTitle}
        {/* left */}
        <div className='flex'>
          <div className={styles.date_count_collection}>
            {/* date */}
            <div className={styles.date}>{rederConvDate}</div>
            <div className={styles.convs_count}>
              <MessageEmojiComIcon className={styles.message_icon} />
              <span>{chatsCount}</span>
            </div>
          </div>
          <div className={styles.chevron__wrapper}>
            <ChevronDownIcon />
          </div>
        </div>
      </div>

      {/* body */}
      <div className={styles.card__body}>{renderChats()}</div>
    </div>
  );
}

export default ConversationCard;
